import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PenTool, Plus, Search, ArrowLeft, Edit, FileText, Users, CalendarDays,
  ListChecks, Hash, Image as ImageIcon, AlertTriangle, CheckCircle2, Save, Cog, Play, Square, FileDown, Tag, Ruler, Trash2, Radio,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  DESIGN_STATUS_LABELS, DESIGN_STATUS_COLORS, PN_TYPE_LABELS,
  type DesignProject, type DesignInputs,
  MACHINING_STATUS_LABELS, MACHINING_STATUS_COLORS,
} from "@/types/design";
import { PartNumberLabelDialog } from "@/components/Design/PartNumberLabelDialog";
import { PnPhotoUpload } from "@/components/Design/PnPhotoUpload";
import { downloadDesignProjectPdf } from "@/lib/designProjectPdf";
import { DesignProjectFormDialog } from "@/components/Design/DesignProjectFormDialog";
import { DesignRevisionDialog } from "@/components/Design/DesignRevisionDialog";
import { PartNumberDialog } from "@/components/Design/PartNumberDialog";
import { MachiningOrderDialog } from "@/components/Design/MachiningOrderDialog";
import { MachiningOrderManage } from "@/components/Design/MachiningOrderManage";

const INPUT_SECTIONS: { key: keyof DesignInputs; label: string }[] = [
  { key: "herramientas", label: "Herramientas disponibles (software/hardware)" },
  { key: "material_disponible", label: "Material disponible" },
  { key: "especificaciones_pieza", label: "Especificaciones de diseño (Pieza)" },
  { key: "especificaciones_master", label: "Especificaciones (Master)" },
  { key: "especificaciones_molde", label: "Especificaciones (Molde)" },
  { key: "especificaciones_utillaje", label: "Especificaciones (Utillajes)" },
  { key: "legales", label: "Entradas legales" },
  { key: "normativas", label: "Entradas normativas (ISO 9001, DIN...)" },
  { key: "items_criticos_diseno", label: "Ítems críticos de diseño" },
  { key: "items_criticos_fabricacion", label: "Ítems críticos de fabricación" },
  { key: "amfe", label: "AMFE" },
];

export default function DesignProjects() {
  const { userRole } = useAuth();
  const canManage = userRole === "admin" || userRole === "supervisor";
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<DesignProject | null>(null);

  // maps de nombres (sin embeds)
  const { data: customers } = useQuery({ queryKey: ["customers-map-d"], queryFn: async () => (await api.from("customers").select("id,name,code")).data || [] });
  const { data: profiles } = useQuery({ queryKey: ["profiles-map-d"], queryFn: async () => (await api.from("profiles").select("id,full_name")).data || [] });
  const { data: projectsMap } = useQuery({ queryKey: ["projects-map-d"], queryFn: async () => (await api.from("projects").select("id,reference_code")).data || [] });
  const custMap = useMemo(() => new Map((customers || []).map((c: any) => [c.id, c])), [customers]);
  const profMap = useMemo(() => new Map((profiles || []).map((p: any) => [p.id, p.full_name])), [profiles]);
  const projMap = useMemo(() => new Map((projectsMap || []).map((p: any) => [p.id, p.reference_code])), [projectsMap]);

  const { data: list } = useQuery({
    queryKey: ["design-projects"],
    queryFn: async () => {
      const { data, error } = await api.from("design_projects").select("*").eq("is_active", true).order("created_at", { ascending: false });
      if (error) throw error;
      return data as DesignProject[];
    },
  });

  const filtered = (list || []).filter((p) =>
    (statusFilter === "all" || p.status === statusFilter) &&
    (search === "" || p.name.toLowerCase().includes(search.toLowerCase()) || (p.code || "").toLowerCase().includes(search.toLowerCase()))
  );

  if (selectedId) {
    return (
      <DashboardLayout>
        <DesignDetail
          id={selectedId}
          onBack={() => setSelectedId(null)}
          canManage={canManage}
          custMap={custMap}
          profMap={profMap}
          onEdit={(p) => { setEditing(p); setFormOpen(true); }}
        />
        <DesignProjectFormDialog open={formOpen} onOpenChange={setFormOpen} project={editing}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["design-project", selectedId] })} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><PenTool className="w-6 h-6" /> Diseño</h1>
            <p className="text-muted-foreground">Proyectos de diseño (procedimiento M_05.14): solicitud, entradas, revisiones, validación y PN.</p>
          </div>
          {canManage && <Button onClick={() => { setEditing(null); setFormOpen(true); }}><Plus className="w-4 h-4 mr-2" />Nuevo proyecto</Button>}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Buscar por nombre o código..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(DESIGN_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <PenTool className="w-12 h-12 mx-auto mb-3 opacity-30" />No hay proyectos de diseño.
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <Card key={p.id} className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setSelectedId(p.id)}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg">{p.name}</CardTitle>
                    <Badge className={DESIGN_STATUS_COLORS[p.status]}>{DESIGN_STATUS_LABELS[p.status]}</Badge>
                  </div>
                  {p.code && <p className="text-sm text-muted-foreground">{p.code}</p>}
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {p.customer_id && <div><span className="text-muted-foreground">Cliente: </span>{custMap.get(p.customer_id)?.name || "—"}</div>}
                  {p.design_lead_id && <div><span className="text-muted-foreground">Responsable: </span>{profMap.get(p.design_lead_id) || "—"}</div>}
                  <div className="text-xs text-muted-foreground pt-1">Creado {format(new Date(p.created_at), "d MMM yyyy", { locale: es })}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <DesignProjectFormDialog open={formOpen} onOpenChange={setFormOpen} project={editing}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["design-projects"] })} />
    </DashboardLayout>
  );
}

// ============== DETALLE ==============
function DesignDetail({ id, onBack, canManage, custMap, profMap, onEdit }: any) {
  const queryClient = useQueryClient();
  const [revOpen, setRevOpen] = useState(false);
  const [pnOpen, setPnOpen] = useState(false);
  const [machOpen, setMachOpen] = useState(false);
  const [manageOrderId, setManageOrderId] = useState<string | null>(null);
  const [labelPn, setLabelPn] = useState<any>(null);
  const [nfcLinkPn, setNfcLinkPn] = useState<any>(null);
  const [availableNfcTags, setAvailableNfcTags] = useState<any[]>([]);
  const [editPn, setEditPn] = useState<any>(null);
  const [measurements, setMeasurements] = useState<any>({});
  const [savingMeas, setSavingMeas] = useState(false);
  const [inputs, setInputs] = useState<DesignInputs>({});
  const [inputsDirty, setInputsDirty] = useState(false);
  const [savingInputs, setSavingInputs] = useState(false);

  const { data: project } = useQuery({
    queryKey: ["design-project", id],
    queryFn: async () => {
      const { data, error } = await api.from("design_projects").select("*").eq("id", id).single();
      if (error) throw error;
      return data as DesignProject;
    },
  });
  const { data: revisions } = useQuery({
    queryKey: ["design-revisions", id],
    queryFn: async () => (await api.from("design_revisions").select("*").eq("design_project_id", id).order("revision_number")).data || [],
  });
  const { data: pns } = useQuery({
    queryKey: ["design-pns", id],
    queryFn: async () => (await api.from("design_part_numbers").select("*").eq("design_project_id", id).order("created_at")).data || [],
  });
  const { data: machOrders } = useQuery({
    queryKey: ["machining-orders", id],
    queryFn: async () => (await api.from("machining_orders").select("*").eq("design_project_id", id).order("created_at", { ascending: false })).data || [],
  });
  const { data: machinesList } = useQuery({
    queryKey: ["machines-map-d"],
    queryFn: async () => (await api.from("machines").select("id,name")).data || [],
  });
  const machMap = new Map((machinesList || []).map((m: any) => [m.id, m.name]));
  const { data: linkedProject } = useQuery({
    queryKey: ["design-linked-project", id],
    queryFn: async () => (await api.from("design_projects").select("project_id").eq("id", id).single()).data,
  });
  const { data: linkedProjectRef } = useQuery({
    queryKey: ["design-linked-project-ref", linkedProject?.project_id],
    queryFn: async () => {
      if (!linkedProject?.project_id) return null;
      const { data } = await api.from("projects").select("reference_code").eq("id", linkedProject.project_id).limit(1);
      return data?.[0] || null;
    },
    enabled: !!linkedProject?.project_id,
  });

  // cargar inputs en estado local cuando llega el proyecto
  useEffect(() => { if (project) { setInputs(project.inputs || {}); setInputsDirty(false); setMeasurements(project.measurements || {}); } }, [project]);

  if (!project) return <div className="py-12 text-center text-muted-foreground">Cargando...</div>;

  const clientCode = project.customer_id ? (custMap.get(project.customer_id)?.code || "") : "";
  const projectCode = (linkedProjectRef?.reference_code || project.code || "");

  const saveInputs = async () => {
    setSavingInputs(true);
    try {
      const { error } = await api.from("design_projects").update({ inputs }).eq("id", id);
      if (error) throw error;
      toast.success("Entradas guardadas");
      setInputsDirty(false);
      queryClient.invalidateQueries({ queryKey: ["design-project", id] });
    } catch (e: any) { toast.error(e.message || "Error al guardar entradas"); }
    finally { setSavingInputs(false); }
  };

  const saveMeasurements = async () => {
    setSavingMeas(true);
    try {
      const { error } = await api.from("design_projects").update({ measurements }).eq("id", id);
      if (error) throw error;
      toast.success("Mediciones guardadas");
      queryClient.invalidateQueries({ queryKey: ["design-project", id] });
    } catch (e: any) { toast.error(e.message || "Error al guardar mediciones"); }
    finally { setSavingMeas(false); }
  };

  const setPnPhoto = async (pnId: string, url: string) => {
    const { error } = await api.from("design_part_numbers").update({ photo_url: url }).eq("id", pnId);
    if (error) { toast.error(error.message); return; }
    toast.success("Imagen del PN actualizada");
    queryClient.invalidateQueries({ queryKey: ["design-pns", id] });
  };

  const deleteProject = async () => {
    if (!window.confirm(`¿Eliminar el proyecto de diseño "${project.name}"? Se marcará como inactivo y se conserva para trazabilidad.`)) return;
    const { error } = await api.from("design_projects").update({ is_active: false }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Proyecto de diseño eliminado (inactivo)");
    onBack();
  };

  const deletePn = async (p: any) => {
    const { data: ots } = await api.from("machining_orders").select("id").eq("design_part_number_id", p.id).limit(1);
    if (ots && ots.length) { toast.error("No se puede eliminar: el PN tiene órdenes de mecanizado asociadas."); return; }
    if (p.validated && (p.linked_mold_id || p.linked_tool_id)) {
      if (!window.confirm(`El PN ${p.pn_code} está validado y vinculado a un molde/utillaje. ¿Eliminar el PN igualmente? (no borra el molde/utillaje)`)) return;
    } else if (!window.confirm(`¿Eliminar el PN ${p.pn_code}? Esta acción no se puede deshacer.`)) return;
    const { error } = await api.from("design_part_numbers").delete().eq("id", p.id);
    if (error) { toast.error(error.message); return; }
    toast.success("PN eliminado");
    queryClient.invalidateQueries({ queryKey: ["design-pns", id] });
  };

  const validatePn = async (p: any) => {
    const uid = getCurrentUser()?.id || null;
    const { error } = await api.from("design_part_numbers").update({ validated: true, validated_at: new Date().toISOString(), validated_by: uid }).eq("id", p.id);
    if (error) { toast.error(error.message); return; }
    try {
      if (p.pn_type === 3 && !p.linked_mold_id) {
        const { data, error: me } = await api.from("molds").insert([{ name: p.name || p.pn_code, code: p.pn_code, customer_id: project.customer_id || null }]).select("id");
        if (me) throw me;
        const moldId = data?.[0]?.id;
        if (moldId) { await api.from("design_part_numbers").update({ linked_mold_id: moldId }).eq("id", p.id); toast.success("PN validado · Molde creado en módulo Moldes"); }
        else toast.success("PN validado");
      } else if (p.pn_type === 4 && !p.linked_tool_id) {
        const { data, error: te } = await api.from("tools").insert([{ name: p.name || p.pn_code, code: p.pn_code }]).select("id");
        if (te) throw te;
        const toolId = data?.[0]?.id;
        if (toolId) { await api.from("design_part_numbers").update({ linked_tool_id: toolId }).eq("id", p.id); toast.success("PN validado · Utillaje creado en Herramientas"); }
        else toast.success("PN validado");
      } else {
        toast.success("PN validado");
      }
    } catch (e: any) {
      toast.warning("PN validado, pero no se pudo crear el registro vinculado: " + (e.message || ""));
    }
    queryClient.invalidateQueries({ queryKey: ["design-pns", id] });
  };

  const nextRev = (revisions || []).reduce((m: number, r: any) => Math.max(m, r.revision_number), 0) + 1;
  const pnById = new Map((pns || []).map((p: any) => [p.id, p]));
  const bomMissingPhotos = (pns || []).filter((p: any) => !p.photo_url).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{project.name}</h1>
              <Badge className={DESIGN_STATUS_COLORS[project.status]}>{DESIGN_STATUS_LABELS[project.status]}</Badge>
            </div>
            {project.code && <p className="text-sm text-muted-foreground">{project.code}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadDesignProjectPdf({ project, revisions: revisions || [], pns: pns || [], customerName: project.customer_id ? custMap.get(project.customer_id)?.name : undefined, directorName: project.director_id ? profMap.get(project.director_id) : undefined, leadName: project.design_lead_id ? profMap.get(project.design_lead_id) : undefined, profMap })}><FileDown className="w-4 h-4 mr-2" />Exportar PDF</Button>
          {canManage && <Button variant="outline" onClick={() => onEdit(project)}><Edit className="w-4 h-4 mr-2" />Editar</Button>}
          {canManage && <Button variant="outline" className="text-destructive" onClick={deleteProject}><Trash2 className="w-4 h-4 mr-2" />Eliminar</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Solicitud */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Solicitud de diseño</CardTitle></CardHeader>
            <CardContent className="text-sm whitespace-pre-wrap">{project.request_summary || <span className="text-muted-foreground">—</span>}</CardContent>
          </Card>

          {/* Entradas */}
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" />Entradas</CardTitle>
              {canManage && <Button size="sm" onClick={saveInputs} disabled={!inputsDirty || savingInputs} className="gap-1"><Save className="w-4 h-4" />Guardar</Button>}
            </CardHeader>
            <CardContent className="space-y-3">
              {INPUT_SECTIONS.map((s) => (
                <div key={s.key}>
                  <Label className="text-xs">{s.label}</Label>
                  <Textarea
                    value={(inputs as any)[s.key] || ""}
                    onChange={(e) => { setInputs({ ...inputs, [s.key]: e.target.value }); setInputsDirty(true); }}
                    rows={2} disabled={!canManage} className="text-sm"
                  />
                  {String(s.key).startsWith("especificaciones") && (
                    <div className="flex items-center gap-2 mt-1">
                      {(inputs as any)[s.key + "_url"] && (
                        <a href={(inputs as any)[s.key + "_url"]} target="_blank" rel="noreferrer" className="text-xs text-primary underline inline-flex items-center gap-1"><FileText className="w-3 h-3" />Ver documento</a>
                      )}
                      {canManage && <PnPhotoUpload size="sm" variant="ghost" label="Adjuntar PDF" onUploaded={(url) => { setInputs({ ...inputs, [s.key + "_url"]: url }); setInputsDirty(true); }} />}
                      {canManage && (
                        <Input value={(inputs as any)[s.key + "_url"] || ""} onChange={(e) => { setInputs({ ...inputs, [s.key + "_url"]: e.target.value }); setInputsDirty(true); }} placeholder="o pega URL del documento..." className="h-7 text-xs flex-1" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Revisiones */}
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" />Revisiones</CardTitle>
              {canManage && <Button size="sm" variant="outline" onClick={() => setRevOpen(true)}><Plus className="w-4 h-4 mr-1" />Añadir</Button>}
            </CardHeader>
            <CardContent className="space-y-2">
              {(revisions || []).length === 0 ? <p className="text-sm text-muted-foreground">Sin revisiones.</p> :
                (revisions || []).map((r: any) => (
                  <div key={r.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">REV {String(r.revision_number).padStart(2, "0")}</span>{r.design_part_number_id && <Badge variant="outline" className="ml-2 text-[10px] font-mono">{pnById.get(r.design_part_number_id)?.pn_code || "PN"}</Badge>}
                      <div className="flex items-center gap-2">
                        <Badge variant={r.status === "approved" ? "default" : "secondary"}>{r.status === "approved" ? "Aprobada" : "Borrador"}</Badge>
                        {r.revision_date && <span className="text-xs text-muted-foreground">{format(new Date(r.revision_date), "d MMM yyyy", { locale: es })}</span>}
                      </div>
                    </div>
                    {r.changes && <p className="text-sm mt-1 whitespace-pre-wrap">{r.changes}</p>}
                    {r.responsible_id && <p className="text-xs text-muted-foreground mt-1">Responsable: {profMap.get(r.responsible_id) || "—"}</p>}
                  </div>
                ))}
            </CardContent>
          </Card>

          {/* PN / BOM */}
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Hash className="w-4 h-4" />Part Numbers / BOM</CardTitle>
              {canManage && <Button size="sm" onClick={() => setPnOpen(true)}><Plus className="w-4 h-4 mr-1" />Generar PN</Button>}
            </CardHeader>
            <CardContent className="space-y-2">
              {bomMissingPhotos > 0 && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 text-sm">
                  <AlertTriangle className="w-4 h-4" /> {bomMissingPhotos} PN sin imagen — revisar y rellenar las fotos de la BOM.
                </div>
              )}
              {(pns || []).length === 0 ? <p className="text-sm text-muted-foreground">Sin PN generados.</p> :
                (pns || []).map((p: any) => (
                  <div key={p.id} className="border rounded-lg p-3 flex items-center gap-3">
                    {p.photo_url ? (
                      /\.pdf($|\?)/i.test(p.photo_url) ? (
                        <a href={p.photo_url} target="_blank" rel="noreferrer" className="w-12 h-12 rounded border flex items-center justify-center text-primary bg-primary/5" title="Ver PDF"><FileText className="w-5 h-5" /></a>
                      ) : (
                        <a href={p.photo_url} target="_blank" rel="noreferrer"><img src={p.photo_url} alt="" className="w-12 h-12 rounded object-cover border" /></a>
                      )
                    ) : (
                      <div className="w-12 h-12 rounded border flex items-center justify-center text-amber-500 bg-amber-500/5" title="Falta imagen"><ImageIcon className="w-5 h-5" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-sm">{p.pn_code}</span>
                        <Badge variant="outline" className="text-xs">{PN_TYPE_LABELS[p.pn_type]}</Badge>
                        {p.validated && <Badge className="bg-green-500/10 text-green-600 border-green-500/20 gap-1"><CheckCircle2 className="w-3 h-3" />Validado</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.name || ""}{p.client_part_pn ? ` · PN cliente: ${p.client_part_pn}` : ""}
                        {p.validated && <span className="block text-[10px] text-green-700">Validado por {profMap.get(p.validated_by) || "—"}{p.validated_at ? ` · ${format(new Date(p.validated_at), "d MMM yyyy", { locale: es })}` : ""}</span>}
                      </div>
                    </div>
                    {canManage && <PnPhotoUpload size="sm" variant="ghost" label={p.photo_url ? "Cambiar foto" : "Foto"} onUploaded={(url) => setPnPhoto(p.id, url)} />}
                    <Button size="sm" variant="ghost" onClick={() => setLabelPn(p)}><Tag className="w-3.5 h-3.5 mr-1" />Etiqueta</Button>
                    <Button size="sm" variant="ghost" onClick={async () => { 
                      const { data } = await api.from('nfc_tags').select('id,tag_uid,label').eq('status', 'active').order('label');
                      setAvailableNfcTags(data || []); 
                      setNfcLinkPn(p); 
                    }}><Radio className="w-3.5 h-3.5 mr-1" />{p.nfc_tag_id ? 'NFC ✓' : 'NFC'}</Button>
                    {canManage && <Button size="sm" variant="ghost" onClick={() => setEditPn(p)}><Edit className="w-3.5 h-3.5 mr-1" />Editar</Button>}
                    {canManage && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePn(p)} title="Eliminar PN"><Trash2 className="w-3.5 h-3.5" /></Button>}
                    {canManage && !p.validated && <Button size="sm" variant="outline" onClick={() => validatePn(p)}>Validar</Button>}
                  </div>
                ))}
            </CardContent>
          </Card>

          {/* Mecanizado */}
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Cog className="w-4 h-4" />Mecanizado (OT)</CardTitle>
              {canManage && <Button size="sm" onClick={() => setMachOpen(true)}><Plus className="w-4 h-4 mr-1" />Nueva OT</Button>}
            </CardHeader>
            <CardContent className="space-y-2">
              {(machOrders || []).length === 0 ? <p className="text-sm text-muted-foreground">Sin órdenes de mecanizado.</p> :
                (machOrders || []).map((o: any) => (
                  <div key={o.id} className="border rounded-lg p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{o.code || "OT mecanizado"}</span>
                        <Badge className={MACHINING_STATUS_COLORS[o.status]}>{MACHINING_STATUS_LABELS[o.status]}</Badge>
                        {o.stock_discounted && <Badge variant="outline" className="text-green-600 border-green-500/30 text-xs">Stock descontado</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {o.machine_id ? `Fresadora: ${machMap.get(o.machine_id) || "—"}` : "Sin fresadora"}
                        {o.material_name ? ` · ${o.material_name} (${o.material_qty} ${o.material_unit || ""})` : ""}
                      </div>
                    </div>
                    {canManage && <Button size="sm" variant="outline" onClick={() => setManageOrderId(o.id)}>Gestionar</Button>}
                  </div>
                ))}
            </CardContent>
          </Card>

          {/* Mediciones y validación final */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Ruler className="w-4 h-4" />Mediciones y validación final</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {["master", "molde", "pieza"].map((k) => (
                <div key={k} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm capitalize">{k}</span>
                    <Select value={measurements?.[k]?.status || "pendiente"} onValueChange={(v) => setMeasurements((m: any) => ({ ...m, [k]: { ...(m?.[k] || {}), status: v } }))}>
                      <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="ok">Conforme (OK)</SelectItem>
                        <SelectItem value="nok">No conforme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea value={measurements?.[k]?.notes || ""} onChange={(e) => setMeasurements((m: any) => ({ ...m, [k]: { ...(m?.[k] || {}), notes: e.target.value } }))} placeholder="Mediciones / observaciones..." className="text-sm min-h-[60px]" disabled={!canManage} />
                </div>
              ))}
              {canManage && <Button size="sm" onClick={saveMeasurements} disabled={savingMeas} className="gap-1"><Save className="w-4 h-4" />{savingMeas ? "Guardando..." : "Guardar mediciones"}</Button>}
            </CardContent>
          </Card>
        </div>

        {/* Lateral: participantes + cronograma */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Participantes</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row k="Cliente" v={project.customer_id ? custMap.get(project.customer_id)?.name : null} />
              <Row k="Propietario" v={project.owner} />
              <Row k="Proveedor" v={project.solution_provider} />
              <Row k="Director" v={project.director_id ? profMap.get(project.director_id) : null} />
              <Row k="Resp. diseño" v={project.design_lead_id ? profMap.get(project.design_lead_id) : null} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarDays className="w-4 h-4" />Cronograma</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row k="Inicio" v={project.start_date ? format(new Date(project.start_date), "d MMM yyyy", { locale: es }) : null} />
              <Row k="Fin estimado" v={project.end_date ? format(new Date(project.end_date), "d MMM yyyy", { locale: es }) : null} />
              {project.schedule_notes && <p className="text-muted-foreground whitespace-pre-wrap pt-1">{project.schedule_notes}</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      <DesignRevisionDialog open={revOpen} onOpenChange={setRevOpen} designProjectId={id} pns={pns || []} revisions={revisions || []}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["design-revisions", id] })} />
      <PartNumberDialog open={pnOpen || !!editPn} onOpenChange={(o) => { if (!o) { setPnOpen(false); setEditPn(null); } }} designProjectId={id}
        defaultClientCode={clientCode} defaultProjectCode={projectCode} pn={editPn}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["design-pns", id] })} />
      <MachiningOrderDialog open={machOpen} onOpenChange={setMachOpen} designProjectId={id} partNumbers={pns || []}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["machining-orders", id] })} />
      <MachiningOrderManage open={!!manageOrderId} onOpenChange={(o) => { if (!o) setManageOrderId(null); }}
        order={(machOrders || []).find((o: any) => o.id === manageOrderId) || null}
        onChanged={() => { queryClient.invalidateQueries({ queryKey: ["machining-orders", id] }); }} />
      <PartNumberLabelDialog open={!!labelPn} onOpenChange={(o) => { if (!o) setLabelPn(null); }} pn={labelPn} customerName={project.customer_id ? custMap.get(project.customer_id)?.name : undefined} />
      
      {/* Dialog para vincular NFC */}
      <Dialog open={!!nfcLinkPn} onOpenChange={(o) => { if (!o) setNfcLinkPn(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular etiqueta NFC al PN</DialogTitle>
            <DialogDescription>PN: {nfcLinkPn?.pn_code}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Label>Seleccionar etiqueta NFC</Label>
            <Select value={nfcLinkPn?.nfc_tag_id || ''} onValueChange={async (v) => {
              const tagId = v === '__none__' ? null : v;
              await api.from('design_part_numbers').update({ nfc_tag_id: tagId }).eq('id', nfcLinkPn.id);
              toast.success(tagId ? 'Etiqueta NFC vinculada' : 'Etiqueta NFC desvinculada');
              refetchPns();
              setNfcLinkPn(null);
            }}>
              <SelectTrigger><SelectValue placeholder="Sin etiqueta NFC" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin etiqueta NFC</SelectItem>
                {availableNfcTags.map((t: any) => (
                  <SelectItem key={t.id} value={t.id}>{t.label || t.tag_uid}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }: { k: string; v: any }) {
  return <div className="flex justify-between gap-2"><span className="text-muted-foreground">{k}:</span><span className="font-medium text-right">{v || "—"}</span></div>;
}
